import React, { useState, useEffect, useRef } from 'react';
import {
	TextField,
	Button,
	Switch,
	FormControlLabel,
	Container,
	Box,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CloseIcon from '@mui/icons-material/Close';
import { ServiceProduct } from 'types/serviceProduct';

interface ServiceProductFormProps {
	serviceProduct?: ServiceProduct;
	open: boolean;
	handleClose: () => void;
	productNameProvide: 'Airtel' | 'Jio' | 'Vi' | 'Tata';
}

const ViewServiceProduct: React.FC<ServiceProductFormProps> = ({
	serviceProduct,
	open,
	handleClose,
	productNameProvide,
}) => {
	const [formData, setFormData] = useState<ServiceProduct>({
		_id: '',
		productName: productNameProvide,
		serviceName: '',
		description: '',
		isPlanStructure: false,
		plans: {},
		textContent: { description: '', image: '' },
		isFancyNumber: false,
		isDTH: false,
		status: 0,
		createdAt: '',
		updatedAt: '',
	});
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const quillRef = useRef<ReactQuill>(null);

	useEffect(() => {
		if (serviceProduct) {
			setFormData({
				...serviceProduct,
				textContent: {
					description: serviceProduct.textContent?.description || '',
					image: serviceProduct.textContent?.image || '',
				},
			});
			if (serviceProduct.textContent?.image) {
				if (typeof serviceProduct.textContent.image === 'string') {
					setImagePreview(serviceProduct.textContent.image);
				} else if (serviceProduct.textContent.image instanceof File) {
					setImagePreview(URL.createObjectURL(serviceProduct.textContent.image));
				}
			}
		}
	}, [serviceProduct]);
	const handleQuillChange = (value: string) => {
		setFormData({
			...formData,
			textContent: {
				...formData.textContent,
				description: value,
			},
		});
	};

	return (
		<Dialog open={open}>
			<DialogTitle variant="h3" style={{ borderBottom: '1px solid gray' }}>
				{serviceProduct && `View ${productNameProvide} Service`}
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{ position: 'absolute', right: 8, top: 8 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<Container>
					<Box sx={{ mt: 0 }}>
						<form>
							<TextField
								label="Service Name"
								name="serviceName"
								value={formData.serviceName}
								fullWidth
								margin="normal"
								required
								size="small"
								disabled
							/>
							<TextField
								label="Service Description"
								name="description"
								value={formData.description}
								fullWidth
								margin="normal"
								size="small"
								disabled
							/>
							<FormControlLabel
								control={
									<Switch
										checked={formData.isPlanStructure}
										name="isPlanStructure"
										disabled
									/>
								}
								label="Is Plan Structure"
							/>
							{formData.isPlanStructure && (
								<>
									<FormControlLabel
										control={
											<Switch
												checked={formData.isDTH}
												name="isDTH"
												disabled
											/>
										}
										label="Is DTH"
									/>
								</>
							)}
							{!formData.isPlanStructure && (
								<>
									<FormControlLabel
										control={
											<Switch
												checked={formData.isFancyNumber}
												name="isFancyNumber"
												disabled
											/>
										}
										color="secondary"
										label="Is Fancy Number"
									/>
									<Typography style={{ marginTop: '10px' }} color="secondary">
										Plan Description
									</Typography>
									<ReactQuill
										ref={quillRef}
										value={formData.textContent.description}
										onChange={handleQuillChange}
										modules={{
											toolbar: [
												[{ header: '1' }, { header: '2' }, { font: [] }],
												[{ size: [] }],
												[
													'bold',
													'italic',
													'underline',
													'strike',
													'blockquote',
												],
												[
													{ list: 'ordered' },
													{ list: 'bullet' },
													{ indent: '-1' },
													{ indent: '+1' },
												],
												['link', 'image', 'video'],
												['clean'],
											],
										}}
										formats={[
											'header',
											'font',
											'size',
											'bold',
											'italic',
											'underline',
											'strike',
											'blockquote',
											'list',
											'bullet',
											'indent',
											'link',
											'image',
											'video',
										]}
										readOnly
									/>
									<div style={{ marginTop: '15px' }}>
										{imagePreview && (
											<>
												<Typography
													style={{ marginTop: '10px' }}
													color="secondary"
												>
													Plan Image:
												</Typography>
												<div
													style={{
														position: 'relative',
														marginTop: '10px',
														display: 'flex',
														justifyContent: 'center',
														marginBottom: '10px',
														border: '1px solid #ccc',
														padding: '10px',
														borderRadius: '8px',
														backgroundColor: '#f9f9f9',
													}}
												>
													<img
														src={imagePreview}
														alt="Preview"
														style={{ width: 'auto', height: '300px' }}
													/>
												</div>
											</>
										)}
									</div>
								</>
							)}

							<DialogActions>
								<Button onClick={handleClose} color="secondary" variant="outlined">
									Cancel
								</Button>
							</DialogActions>
						</form>
					</Box>
				</Container>
			</DialogContent>
		</Dialog>
	);
};

export default ViewServiceProduct;
